/**
 * ContentComponentOverlays
 *
 * Full-size overlay rendered over the content video area. Mirrors Flutter's
 * `content_widget_video_overlays.dart` with three visual states:
 *
 *   1. Not initialized — PlayPauseAnimatedButton (play icon) + static progress
 *   2. Initializing     — AnimatedLoadingIndicator + static progress
 *   3. Initialized      — Replay / Play-Pause / Skip + live progress + fullscreen
 *
 * Visibility:
 *   - Shown when video is not initialized OR not playing
 *   - When `autoHide` is true (fullscreen), uses auto-hide timeout logic
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/presentation/store';
import { useResponsiveDesign, isMobileViewport, useIsLandscape } from '@/presentation/responsiveDesign/responsiveDesign';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { UnlockContentPreviewCarousel } from '@/presentation/feature/content/components/unlock/UnlockContentPreviewCarousel';

import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { ExpauseButton, ButtonVariant, ButtonSize, ButtonIconPosition } from '@/presentation/components/common/controls/ExpauseButton';
import { PlayPauseAnimatedButton } from '@/presentation/components/video/PlayPauseAnimatedButton';
import { VideoPlayblackCurrentProgress } from '@/presentation/components/video/VideoPlayblackCurrentProgress';
import { VideoFullscreenButton } from '@/presentation/components/video/VideoFullscreenButton';
import { useVideoManagement } from '@/presentation/feature/content/hooks/useVideoManagement';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { VideoPlaybackState } from '@/presentation/store/slices/videoManagement/videoManagementSlice';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { ExpauseLoadingIndicator } from '../common/loadingIndicators/ExpauseLoadingIndicator';
import { NsfwGateOverlay } from '@/presentation/components/video/NsfwGateOverlay';

export interface ContentComponentOverlaysProps {
  /** Screen key used to compose the video key */
  screenKey: string;

  /** Media entity backing the video */
  media: UserMediaEntity;

  /** Current playback state for this video (from videoManagement) */
  playbackState: VideoPlaybackState | undefined;

  /** Media duration in seconds (for static progress display pre-init) */
  duration: number;

  /** True once video playback has actually started (onPlaybackStart fired) */
  isVideoReady: boolean;

  /** Whether to auto-hide overlay when playing (fullscreen mode) */
  autoHide?: boolean;

  /** Callback when fullscreen button is clicked */
  onFullscreenClick?: () => void;

  /** Custom class name */
  className?: string;

  /** Whether the current user has exclusive rights (unlocked or subscribed) */
  hasExclusiveRights?: boolean;

  /** Whether playback has reached the typeLimit gate */
  typeLimitReached?: boolean;

  /** Callback when the unlock button is tapped in the locked overlay */
  onUnlockContentTap?: () => void;

  /** Whether the NSFW (explicit content) gate is active — playback held until reveal */
  nsfwGated?: boolean;

  /** Callback to reveal NSFW content and resume playback */
  onNsfwReveal?: () => void;
}

export const ContentComponentOverlays: React.FC<ContentComponentOverlaysProps> = ({
  screenKey,
  media,
  playbackState,
  duration,
  isVideoReady,
  autoHide = false,
  onFullscreenClick,
  className = '',
  hasExclusiveRights = false,
  typeLimitReached = false,
  onUnlockContentTap,
  nsfwGated = false,
  onNsfwReveal,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const isLandscape = useIsLandscape();
  const { togglePlayPauseActiveVideo, replayActiveVideo, skipToExclusiveContent } = useVideoManagement();

  const videoKey = MediaUtils.composeVideoPlayerKey(screenKey, media.id);

  // Derive video states from the video slice
  const videoState = useSelector((state: RootState) => state.video.videos[videoKey]);
  const isInitialized = !!(
    videoState &&
    !videoState.isLoading &&
    !videoState.error &&
    !videoState.isDisposed &&
    videoState.initializedAt > 0
  );
  const isLoading = videoState?.isLoading || false;

  const isPlaying = playbackState?.isPlaying ?? false;

  // Auto-hide logic (kept from VideoControls for fullscreen use)
  const [showControls, setShowControls] = useState(true);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetHideTimeout = useCallback(() => {
    if (!autoHide) return;

    setShowControls(true);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);

    if (isPlaying) {
      hideTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, ANIMATION_CONSTANTS.duration.autoHideDelay);
    }
  }, [autoHide, isPlaying]);

  useEffect(() => {
    if (!isPlaying) {
      setShowControls(true);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    } else if (autoHide) {
      resetHideTimeout();
    }
  }, [isPlaying, autoHide, resetHideTimeout]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  // When playback limit is reached and user has no rights, show unlock overlay
  const showUnlockOverlay = typeLimitReached && !hasExclusiveRights;

  // Visibility: show the overlay whenever the video is not yet initialized OR
  // is currently paused. In auto-hide mode (fullscreen), the auto-hide timer
  // still gets the final say while the video is actively playing.
  // Also force-show when the unlock or NSFW overlay is active.
  const baseShouldShow = !isInitialized || !isPlaying || showUnlockOverlay || nsfwGated;
  const shouldShow = autoHide ? baseShouldShow || showControls : baseShouldShow;

  const isLocked = media.unlockCharge != null && media.unlockCharge > 0;
  const canSkip =
    isLocked &&
    media.typeLimit != null &&
    !typeLimitReached;

  const handlePlayPauseTap = useCallback(() => {
    if (showUnlockOverlay) return;
    void togglePlayPauseActiveVideo();
  }, [showUnlockOverlay, togglePlayPauseActiveVideo]);

  const handleReplayTap = useCallback(() => {
    if (showUnlockOverlay) return;
    void replayActiveVideo();
  }, [showUnlockOverlay, replayActiveVideo]);

  const handleSkipTap = useCallback(() => {
    if (showUnlockOverlay || !canSkip) return;
    void skipToExclusiveContent(media);
  }, [showUnlockOverlay, canSkip, skipToExclusiveContent, media]);

  // ── Center content: 3-state rendering ──
  // While the NSFW gate is active, suppress all playback controls — only the
  // reveal CTA is interactable.
  const showLoading = !nsfwGated && (isLoading || (isInitialized && !isVideoReady));
  const showPlayPause = !showLoading && !nsfwGated;
  const showReplaySkip = isInitialized && !showLoading && !nsfwGated;

  return (
    <div
      className={`absolute inset-0 flex flex-col ${className}`}
      style={{
        pointerEvents: 'none',
        opacity: shouldShow ? 1 : 0,
        transition: `opacity ${ANIMATION_CONSTANTS.duration.slow}ms ${ANIMATION_CONSTANTS.easing.easeOut}`,
      }}
      onMouseMove={autoHide ? resetHideTimeout : undefined}
      onTouchStart={autoHide ? resetHideTimeout : undefined}
    >
      {/* Blurred preview background when content is locked at typeLimit */}
      {showUnlockOverlay && (() => {
        // Match the effective aspect ratio from VideoComponent's getVideoWrapperStyle
        const videoIsLandscape = MediaUtils.isLandscapeVideo(media);
        const deviceIsMobile = isMobileViewport();
        const effectiveAspectRatio = videoIsLandscape
          ? (deviceIsMobile && !isLandscape ? media.aspectRatio.toString() : '21/9')
          : (deviceIsMobile ? '3/4' : media.aspectRatio.toString());

        return (
          <div className="absolute inset-0 flex items-center justify-center" style={{ pointerEvents: 'auto' }}>
            <UnlockContentPreviewCarousel
              media={media}
              style={{
                height: '100%',
                aspectRatio: effectiveAspectRatio,
                maxWidth: '100%',
                borderRadius: isLandscape && videoIsLandscape ? responsive.borderRadius.base : undefined,
                overflow: 'hidden',
              }}
            />
          </div>
        );
      })()}

      {/* Center: playback controls — absolutely centered over the full overlay */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          gap: responsive.gap['3xl'],
          pointerEvents: 'none',
        }}
      >
        {showLoading && (
          <ExpauseLoadingIndicator
            size={responsive.button.size.medium - responsive.spacing.lg}
            addInsideCircleParent={true}
            circleParentColor={`${theme.black}66`}
            circleParentSize={responsive.icon.large * 2 + responsive.spacing.lg - 6}
          />
        )}

        {showReplaySkip && !showUnlockOverlay && (
          <ExpauseButton
            variant={ButtonVariant.round}
            size={ButtonSize.medium}
            icon="/assets/images/replay.png"
            iconPosition={ButtonIconPosition.center}
            onClick={shouldShow ? handleReplayTap : undefined}
            style={{ pointerEvents: 'auto' }}
          />
        )}

        {/* Unlock button replaces play/pause when typeLimit is reached */}
        {showUnlockOverlay ? (
          <ExpauseButton
            variant={ButtonVariant.round}
            icon="/assets/vectors/lock-filled.svg"
            backgroundColor={`${theme.black}66`}
            iconColor={theme.exclusive}
            iconPosition={ButtonIconPosition.center}
            onClick={shouldShow && onUnlockContentTap ? onUnlockContentTap : undefined}
            style={{ pointerEvents: 'auto' }}
          />
        ) : (
          <>
            {/* key prevents remount when replay/skip siblings appear/disappear */}
            <PlayPauseAnimatedButton
              key="play-pause-btn"
              isPlaying={isPlaying}
              onClick={showPlayPause && shouldShow ? handlePlayPauseTap : undefined}
              style={{
                pointerEvents: showPlayPause && !isInitialized ? 'none' : showPlayPause ? 'auto' : 'none',
                display: showPlayPause ? undefined : 'none',
              }}
            />
            {showReplaySkip && !showUnlockOverlay && (
              <ExpauseButton
                variant={ButtonVariant.round}
                size={ButtonSize.medium}
                icon="/assets/images/skip.png"
                iconPosition={ButtonIconPosition.center}
                onClick={shouldShow ? handleSkipTap : undefined}
                disabled={!canSkip}
                style={{ pointerEvents: 'auto' }}
              />
            )}
          </>
        )}
      </div>

      {/* NSFW gate: full-area reveal layer. Mirrors Flutter's
          `_checkExplicitContentUpdates` + `showExplicitContent` flow. Only
          the "Show anyway" button inside is interactive. */}
      {nsfwGated && onNsfwReveal && (
        <NsfwGateOverlay onReveal={onNsfwReveal} />
      )}

      {/* Spacer to push bottom strip to the bottom */}
      <div className="flex-1" />

      {/* Bottom strip: progress time + fullscreen */}
      <div
        style={{
          background: `linear-gradient(to top, ${theme.black}B3 0%, ${theme.black}00 100%)`,
          paddingTop: responsive.spacing['3xl'],
          paddingBottom: responsive.spacing.lg,
          pointerEvents: 'none',
        }}
      >
        <div className="flex items-center justify-between" style={{ paddingLeft: responsive.spacing['2xl'], paddingRight: responsive.spacing['2xl'] }}>
          {/* Left spacer */}
          <div className="flex-1 flex items-center" />

          {/* Center: playback time */}
          <div
            className="flex items-center rounded-full"
            style={{
              backgroundColor: `${theme.surface}E6`,
              paddingLeft: responsive.spacing.base,
              paddingRight: responsive.spacing.base,
              paddingTop: responsive.spacing.xs,
              paddingBottom: responsive.spacing.xs,
            }}
          >
            {isInitialized ? (
              <VideoPlayblackCurrentProgress
                videoKey={videoKey}
                duration={playbackState?.duration ?? duration}
              />
            ) : (
              <div style={{ color: theme.white, fontSize: responsive.font.sm, fontWeight: responsive.fontWeight.medium }}>
                {MediaUtils.formatVideoProgress(0, duration)}
              </div>
            )}
          </div>

          {/* Right: fullscreen toggle (post-init only) */}
          <div className="flex-1 flex items-center justify-end">
            {isVideoReady && onFullscreenClick && (
              <VideoFullscreenButton onClick={onFullscreenClick} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
